import {
  getDatadogEnv,
  getDatadogRumApplicationId,
  getDatadogRumClientToken,
  getDatadogRumSessionSampleRate,
  getDatadogRumSessionReplaySampleRate,
  getDatadogService,
  getDatadogSite,
  getDatadogVersion,
  isDatadogLogsConfigured,
  isDatadogRumConfigured,
  isE2eTestMode,
} from "@/lib/datadog/config";
import { getRumAllowedTracingUrls } from "@/lib/datadog/rumAllowedTracingUrls";

function shouldInitDatadogClient(): boolean {
  return !isE2eTestMode() && (isDatadogRumConfigured() || isDatadogLogsConfigured());
}

let initialized = false;

async function initDatadogClient(): Promise<void> {
  if (initialized || !shouldInitDatadogClient()) {
    return;
  }
  initialized = true;

  const datadogClientConfig = {
    clientToken: getDatadogRumClientToken()!,
    site: getDatadogSite(),
    service: getDatadogService(),
    env: getDatadogEnv(),
    version: getDatadogVersion(),
  };

  if (isDatadogLogsConfigured()) {
    const { datadogLogs } = await import("@datadog/browser-logs");
    datadogLogs.init({
      ...datadogClientConfig,
      forwardErrorsToLogs: true,
      sessionSampleRate: getDatadogRumSessionSampleRate(),
    });
  }

  if (isDatadogRumConfigured()) {
    const [{ datadogRum }, { nextjsPlugin }] = await Promise.all([
      import("@datadog/browser-rum"),
      import("@datadog/browser-rum-nextjs"),
    ]);

    datadogRum.init({
      applicationId: getDatadogRumApplicationId()!,
      ...datadogClientConfig,
      sessionSampleRate: getDatadogRumSessionSampleRate(),
      sessionReplaySampleRate: getDatadogRumSessionReplaySampleRate(),
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      plugins: [nextjsPlugin()],
      allowedTracingUrls: getRumAllowedTracingUrls(),
    });
  }
}

void initDatadogClient();

type RouterTransitionHandler = (
  href: string,
  navigationType: "push" | "replace" | "traverse",
) => void | Promise<void>;

export const onRouterTransitionStart: RouterTransitionHandler = async (href) => {
  if (!isDatadogRumConfigured() || isE2eTestMode()) {
    return;
  }

  const { onRouterTransitionStart: trackRouterTransition } = await import(
    "@datadog/browser-rum-nextjs"
  );
  return trackRouterTransition(href);
};
